import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Query keys start with 'operations' so the server's SSE invalidation (['operations', …]) reaches them.
export const opsKeys = {
  all: ['operations'] as const,
  facilities: () => ['operations', 'facilities'] as const,
  bookings: (from: string, to: string) => ['operations', 'bookings', from, to] as const,
  duties: (from: string, to: string, order: 'asc' | 'desc') => ['operations', 'duties', from, to, order] as const,
  people: (date: string) => ['operations', 'people', date] as const,
  equipment: () => ['operations', 'equipment'] as const,
  board: () => ['operations', 'board'] as const,
  travel: (teamId: string, season: string) => ['operations', 'travel', teamId, season] as const,
}

export const facilitiesQuery = () =>
  queryOptions({ queryKey: opsKeys.facilities(), queryFn: () => unwrap(api.facilities.$get()) })

export const bookingsQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: opsKeys.bookings(from, to),
    queryFn: () => unwrap(api.facilities.bookings.$get({ query: { from, to } })),
    placeholderData: (prev) => prev,
  })

export const dutiesQuery = (from: string, to: string, order: 'asc' | 'desc' = 'asc') =>
  queryOptions({
    queryKey: opsKeys.duties(from, to, order),
    queryFn: () => unwrap(api.duties.$get({ query: { from, to, order } })),
    placeholderData: (prev) => prev,
  })

export const peopleQuery = (date: string) =>
  queryOptions({ queryKey: opsKeys.people(date), queryFn: () => unwrap(api.ops.people.$get({ query: { date } })) })

export const equipmentQuery = () =>
  queryOptions({ queryKey: opsKeys.equipment(), queryFn: () => unwrap(api.equipment.$get()) })

export const boardQuery = () => queryOptions({ queryKey: opsKeys.board(), queryFn: () => unwrap(api['org-roles'].$get()) })

export const travelQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: opsKeys.travel(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].travel.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })

export type Facilities = Awaited<ReturnType<NonNullable<ReturnType<typeof facilitiesQuery>['queryFn']>>>
export type Facility = Facilities['facilities'][number]
export type Bookings = Awaited<ReturnType<NonNullable<ReturnType<typeof bookingsQuery>['queryFn']>>>
export type Occupancy = Bookings['items'][number]
export type Duties = Awaited<ReturnType<NonNullable<ReturnType<typeof dutiesQuery>['queryFn']>>>
export type Duty = Duties['other'][number]
export type DutyMatch = Duties['matches'][number]
export type Template = Duties['templates'][number]
export type OpsPerson = Awaited<ReturnType<NonNullable<ReturnType<typeof peopleQuery>['queryFn']>>>[number]
export type Equipment = Awaited<ReturnType<NonNullable<ReturnType<typeof equipmentQuery>['queryFn']>>>
export type EquipmentItem = Equipment['items'][number]
export type BoardNode = Awaited<ReturnType<NonNullable<ReturnType<typeof boardQuery>['queryFn']>>>[number]
export type Travel = Awaited<ReturnType<NonNullable<ReturnType<typeof travelQuery>['queryFn']>>>
export type TravelMatch = Travel['matches'][number]
export type TravelRules = Travel['settings']



/** Mutation that refetches the given operations keys afterwards (SSE does the same for other tabs). */
export function useOpsMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>, keys: readonly (readonly string[])[] = [opsKeys.all]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSettled: () => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k }))),
  })
}

// --- Calls ------------------------------------------------------------------------------------

type FacilityInput = Parameters<typeof api.facilities.$post>[0]['json']
type BookingInput = Parameters<typeof api.facilities.bookings.$post>[0]['json']
type BookingPatch = Parameters<(typeof api.facilities.bookings)[':id']['$patch']>[0]['json']
type DutyInput = Parameters<typeof api.duties.$post>[0]['json']
type DutyPatch = Parameters<(typeof api.duties)[':id']['$patch']>[0]['json']
type TemplateInput = Parameters<typeof api.duties.templates.$post>[0]['json']
type EquipmentInput = Parameters<typeof api.equipment.$post>[0]['json']
type BoardInput = Parameters<(typeof api)['org-roles']['$post']>[0]['json']

export const ops = {
  createFacility: (json: FacilityInput) => unwrap(api.facilities.$post({ json })),
  updateFacility: ({ id, ...json }: Partial<FacilityInput> & { id: string }) => unwrap(api.facilities[':id'].$patch({ param: { id }, json })),
  archiveFacility: (id: string) => unwrap(api.facilities[':id'].$delete({ param: { id } })),

  createBooking: (json: BookingInput) => unwrap(api.facilities.bookings.$post({ json })),
  updateBooking: ({ id, ...json }: BookingPatch & { id: string }) => unwrap(api.facilities.bookings[':id'].$patch({ param: { id }, json })),
  cancelBooking: ({ id, series }: { id: string; series: boolean }) =>
    unwrap(api.facilities.bookings[':id'].cancel.$post({ param: { id }, json: { series } })),
  restoreBookings: (json: { ids: string[]; status?: 'confirmed' | 'tentative' }) => unwrap(api.facilities.bookings.restore.$post({ json })),

  createDuty: (json: DutyInput) => unwrap(api.duties.$post({ json })),
  updateDuty: ({ id, ...json }: DutyPatch & { id: string }) => unwrap(api.duties[':id'].$patch({ param: { id }, json })),
  deleteDuty: (id: string) => unwrap(api.duties[':id'].$delete({ param: { id } })),
  respondDuty: ({ id, response }: { id: string; response: 'confirm' | 'decline' }) =>
    unwrap(api.duties[':id'].respond.$post({ param: { id }, json: { response } })),
  generateDuties: (json: { templateId: string; matchIds?: string[] }) => unwrap(api.duties.generate.$post({ json })),
  createTemplate: (json: TemplateInput) => unwrap(api.duties.templates.$post({ json })),
  updateTemplate: ({ id, ...json }: Partial<TemplateInput> & { id: string }) => unwrap(api.duties.templates[':id'].$patch({ param: { id }, json })),
  deleteTemplate: (id: string) => unwrap(api.duties.templates[':id'].$delete({ param: { id } })),

  createEquipment: (json: EquipmentInput) => unwrap(api.equipment.$post({ json })),
  updateEquipment: ({ id, ...json }: Partial<EquipmentInput> & { id: string }) => unwrap(api.equipment[':id'].$patch({ param: { id }, json })),
  archiveEquipment: (id: string) => unwrap(api.equipment[':id'].$delete({ param: { id } })),
  restoreEquipment: (id: string) => unwrap(api.equipment[':id'].restore.$post({ param: { id } })),

  createRole: (json: BoardInput) => unwrap(api['org-roles'].$post({ json })),
  updateRole: ({ id, ...json }: Partial<BoardInput> & { id: string }) => unwrap(api['org-roles'][':id'].$patch({ param: { id }, json })),
  moveRole: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
    unwrap(api['org-roles'][':id'].move.$post({ param: { id }, json: { direction } })),
  deleteRole: (id: string) => unwrap(api['org-roles'][':id'].$delete({ param: { id } })),

  payTravel: ({ teamId, season, items }: { teamId: string; season: string; items: { matchId: string; personId: string }[] }) =>
    unwrap(api.teams[':teamId'].travel.payouts.$post({ param: { teamId }, query: { season }, json: { items } })),
  reverseTravel: (ledgerEntryIds: string[]) => unwrap(api.travel.payouts.reverse.$post({ json: { ledgerEntryIds } })),
  saveTravelRules: (json: TravelRules) => unwrap(api.travel.settings.$put({ json })),
  setDrivers: ({ teamId, matchId, personIds }: { teamId: string; matchId: string; personIds: string[] }) =>
    unwrap(api.teams[':teamId'].travel.matches[':matchId'].drivers.$put({ param: { teamId, matchId }, json: { personIds } })),
}

export { loaderScope } from '~/lib/session'
